// Данные дашборда: ответы /api/dashboard и помесячные ряды для экрана.
// Классы строк и группы считает БД (0025); здесь — только раскладка по месяцам и
// включение/выключение строк по переключателям («выплаты собственнику», «CAPEX»).

import { monthsBetween, sum } from './fmt';

export interface Meta {
  clinic: string;
  today: string;
  legal_entities: { id: string; code: string; name: string }[];
  groups: { code: string; name: string; capex: boolean; payroll: boolean }[];
  first_month: string | null;
  last_month: string | null;
  sources: { source: string; last_import_at: string | null; error: string | null; errors: number; warnings: number }[];
}

export type Line = 'rev_cash' | 'rev_bank' | 'rev_other' | 'refund' | 'opex' | 'capex' | 'owner' | 'unmapped' | 'transfer';
export interface MonthlyRow { month: string; line: Line; group_code: string | null; amount: number }

/** Строка расходов: группа справочника или особая линия. */
export interface CostRow {
  key: string;
  name: string;
  /** Как открыть операции этой строки. */
  drill: LedgerFilter;
  special: 'owner' | 'capex' | null;
  payroll: boolean;
  values: number[];
}

export interface Series {
  months: string[];
  cash: number[];
  bank: number[];
  other: number[];
  costs: CostRow[];
}

export interface Toggles {
  /** Выплаты собственнику — не расход. */
  own: boolean;
  /** Капзатраты — не расход. */
  cap: boolean;
}

export interface LedgerFilter {
  q?: string;
  type?: 'all' | 'inc' | 'exp';
  line?: Line;
  group?: string;
  category?: string;
  pay?: 'cash' | 'bank' | 'none';
  from?: string;
  to?: string;
  day?: string;
}

export function buildSeries(meta: Meta, rows: MonthlyRow[]): Series {
  const first = (meta.first_month ?? meta.today).slice(0, 7);
  const last = (meta.last_month ?? meta.today).slice(0, 7);
  const months = monthsBetween(first, last);
  const idx = new Map(months.map((m, i) => [m, i]));
  const zero = () => months.map(() => 0);
  const cash = zero(), bank = zero(), other = zero();
  const groups = new Map(meta.groups.map((g) => [g.code, zero()]));
  const owner = zero(), refund = zero(), unmapped = zero();
  const ungrouped = zero();
  for (const r of rows) {
    const i = idx.get(r.month);
    if (i === undefined) continue;
    const a = r.amount;
    switch (r.line) {
      case 'rev_cash': cash[i] += a; break;
      case 'rev_bank': bank[i] += a; break;
      case 'rev_other': other[i] += a; break;
      case 'owner': owner[i] += a; break;
      case 'refund': refund[i] += a; break;
      case 'unmapped': unmapped[i] += a; break;
      case 'opex':
      case 'capex': {
        const g = r.group_code ? groups.get(r.group_code) : undefined;
        (g ?? ungrouped)[i] += a;
        break;
      }
    }
  }
  const costs: CostRow[] = meta.groups.map((g) => ({
    key: g.code,
    name: g.name,
    drill: { type: 'exp', group: g.code },
    special: g.capex ? 'capex' : null,
    payroll: g.payroll,
    values: groups.get(g.code)!,
  }));
  if (sum(ungrouped)) costs.push({ key: '_ungrouped', name: 'Статьи вне групп', drill: { type: 'exp' }, special: null, payroll: false, values: ungrouped });
  costs.push({ key: '_owner', name: 'Выплаты собственнику', drill: { line: 'owner' }, special: 'owner', payroll: false, values: owner });
  costs.push({ key: '_refund', name: 'Возвраты пациентам', drill: { line: 'refund' }, special: null, payroll: false, values: refund });
  costs.push({ key: '_unmapped', name: 'Без статьи — проверить', drill: { line: 'unmapped' }, special: null, payroll: false, values: unmapped });
  return { months, cash, bank, other, costs };
}

export const revenue = (s: Series, i: number) => s.cash[i] + s.bank[i] + s.other[i];

/** Строка входит в расходы при текущих переключателях. */
export const counts = (c: CostRow, t: Toggles) => !((t.own && c.special === 'owner') || (t.cap && c.special === 'capex'));

export const expenses = (s: Series, i: number, t: Toggles) =>
  s.costs.reduce((acc, c) => acc + (counts(c, t) ? c.values[i] : 0), 0);

export const range = (a: number, b: number) => Array.from({ length: b - a + 1 }, (_, k) => a + k);
